import React, { useEffect, useState } from 'react';
import { Box, Button, makeStyles, Typography } from '@material-ui/core';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CallMadeRoundedIcon from '@material-ui/icons/CallMadeRounded';
import { SCREENS } from '../../constants/Screens';
import { CommonColor } from '../../constants/Colors';
import { myRankingData } from '../../models/RankingModel';

/**
 * Props
 */
interface Props {
  myNowRanking?: myRankingData;
  isLoading: boolean;
}

/************************************************
 * function
 * *********************************************
 */

//  「ランク」ボタン：ランキング一覧へ遷移
const onhandleRankingButton = () => {
  window.navi.navigate(SCREENS.HOME.RANKING);
};

const MyNowRanking: React.FC<Props> = ({ myNowRanking, isLoading }) => {
  const classes = useStyles();

  /** 今月のランキング順位（受領済の金額） */
  const thisMonthRanking_received = myNowRanking?.myRank_totalReceived;
  /** 今月のランキング順位（総額） */
  const thisMonthRanking_forecast = myNowRanking?.myRank_totalForecasted;

  /** 前月のランキング順位（受領済の金額） */
  const prevMonthRanking = myNowRanking?.myRank_totalReceived_prevMonth;
  /** 前年（前年同月）のランキング順位（受領済の金額） */
  const prevYearRanking = myNowRanking?.myRank_totalReceived_prevYear;

  /** 前月アイコン */
  const [prevMonthIcon, setPrevMonthIcon] = useState('');
  /** 前年（前年同月）アイコン */
  const [prevYearIcon, setPrevYearIcon] = useState('');

  /** 前月比較・アイコン名称の設定 */
  const rankingIcon = (
    thisMonthRanking: number | null | undefined,
    prevMonthRanking: number | null | undefined
  ) => {
    if (thisMonthRanking === null || prevMonthRanking === null) return;
    if (thisMonthRanking === undefined || prevMonthRanking === undefined) return;
    if (thisMonthRanking > prevMonthRanking) {
      return 'rankIconDown';
    } else if (thisMonthRanking === prevMonthRanking) {
      return 'rankIconStay';
    } else if (thisMonthRanking < prevMonthRanking) {
      return 'rankIconUp';
    }
  };

  /** ランキングアイコンのセット */
  const prevMonthRankingIcon = rankingIcon(thisMonthRanking_received, prevMonthRanking) ?? '';
  const prevYearRankingIcon = rankingIcon(thisMonthRanking_received, prevYearRanking) ?? '';

  useEffect(() => {
    if (prevMonthRankingIcon) setPrevMonthIcon(prevMonthRankingIcon);
    if (prevYearRankingIcon) setPrevYearIcon(prevYearRankingIcon);
  });

  return (
    <Box className={classes.container}>
      <Box className={classes.rankingBox}>
        <Box className={classes.rankWrapper}>
          <Box className={classes.rankThisMonth}>
            <Box className={classes.rankMainWrapper}>
              <Typography className={classes.rankMain}>
                {!isLoading && thisMonthRanking_received ? thisMonthRanking_received : 'ー '}位
              </Typography>
              <Typography className={classes.rankMain}>
                ({!isLoading && thisMonthRanking_forecast ? thisMonthRanking_forecast : 'ー '}位)
              </Typography>
            </Box>
          </Box>
          <Box className={classes.justifyBetween}>
            <Typography variant="body2">
              前月({!isLoading && prevMonthRanking ? `${prevMonthRanking}位` : ' － '}
              {!isLoading && prevMonthRankingIcon && (
                <CallMadeRoundedIcon
                  fontSize="small"
                  className={classes[prevMonthIcon as keyof typeof classes]}
                />
              )}
              )
            </Typography>
            <Typography variant="body2">
              {/* 前年＝前年同月 */}
              前年({!isLoading && prevYearRanking ? `${prevYearRanking}位` : ' － '}
              {!isLoading && prevYearRankingIcon && (
                <CallMadeRoundedIcon
                  fontSize="small"
                  className={classes[prevYearIcon as keyof typeof classes]}
                />
              )}
              )
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          className={classes.button}
          aria-label="ランキング一覧を見る"
          onClick={onhandleRankingButton}
        >
          ランク一覧
          <ArrowForwardIosIcon fontSize="small" className={classes.icon} />
        </Button>
      </Box>
      {isLoading ? (
        ''
      ) : myNowRanking?.userId ? (
        ''
      ) : (
        <Typography variant="body2" className={classes.rankingErrorMessage}>
          ※部門設定がされていません。本部画面【ユーザー管理】から設定してください。
        </Typography>
      )}
    </Box>
  );
};

/************************************************
 * style
 * *********************************************
 */
const useStyles = makeStyles({
  container: {
    marginBottom: '10px',
    padding: '10px 5px',
    backgroundColor: CommonColor.Light.BlueGreen,
    border: `1px solid ${CommonColor.Normal.BlueGreen}`,
    borderRadius: '5px',
  },
  rankingBox: {
    display: 'flex',
    justifyContent: 'center',
    gap: '2%',
    '@media (min-width: 600px)': {
      gap: '5%',
    },
  },
  rankingErrorMessage: {
    marginTop: '5px',
    fontSize: '0.7rem',
    color: CommonColor.Normal.Red,
  },
  rankWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: '10px',
    gap: '5px',
  },
  rankThisMonth: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  rankMainWrapper: {
    display: 'flex',
    gap: '5px',
  },
  rankMain: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    lineHeight: '1.3',
  },
  justifyBetween: {
    display: 'flex',
    maxWidth: '350px',
    gap: '5px',
    alignItems: 'end',
  },
  button: {
    backgroundColor: CommonColor.Normal.BlueGreen,
    color: 'white',
    padding: '5px 10px',
    fontSize: '0.5rem',
  },
  icon: {
    marginLeft: '5px',
    fontSize: '0.8rem',
  },
  rankIconUp: {
    fontSize: '1rem',
    color: CommonColor.Normal.Red,
  },
  rankIconStay: {
    fontSize: '1rem',
    color: CommonColor.Normal.BlueGreen,
    rotate: '45deg',
  },
  rankIconDown: {
    fontSize: '1rem',
    color: CommonColor.Normal.Blue,
    rotate: '90deg',
  },
});

export default MyNowRanking;
